import type {
    CalendarEntry,
    CalendarEventData,
    CalendarGroupFilter,
    CalendarList,
    CalendarListFilter,
    CalendarPayload,
} from "@qendo/calendar";
import type { PermissionProps } from "@qendo/permissions";
import type { JournalData, JournalPayload, PeriodPayload } from "@qendo/profile/journal";
import { TrackableType } from "@qendo/trackables";
import type { ListData } from "@qendo/types";
import { ucfirst } from "@samlevy/string-helpers";
import { type UseQueryResult, useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";
import {
    compareDesc,
    type DateArg,
    endOfMonth,
    isAfter,
    isBefore,
    isSameDay,
    isSameMonth,
    startOfDay,
    subMonths,
} from "date-fns";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CalendarContext } from "react-native-calendars";
import type { MarkedDates } from "react-native-calendars/src/types";
import type { CurrentCycleData } from "~/entities/profile/current-cycle-data";
import { resolveStoreMethod, useApi } from "~/providers/ApiProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { filterUnique } from "~/util/array";
import {
    formatDateString,
    formatDateTimeStartOfMinute,
    formatDateTimeStartOfSecond,
    formatDateTimeString,
    getMonth,
    getMonthsForWeek,
    mergeDateTime,
} from "~/util/dates";
import {
    filterCalendar,
    filterCalendarForMonth,
    filterCalendarForWeek,
    markDates,
    resolveRecurrences,
    sortCalendar,
} from "~/util/qendo/calendar";

import { journalPermissionSection } from "~/util/qendo/permissions";

declare interface DestroyJournalPayload {
    type: TrackableType;
    id: string;
    recorded_on: DateArg<Date>;
}

declare type JournalState = {
    journals: CalendarList<JournalData>;
    events: CalendarList<CalendarEventData>;
    marked: MarkedDates;
    recentlyLogged: string[];
    modified: boolean;
    refreshJournals(date?: DateArg<Date> | null | undefined): Promise<void>;
    refreshEvents(date?: DateArg<Date> | null | undefined): Promise<void>;
    refreshCalendar(date?: DateArg<Date> | null | undefined): Promise<void>;
    loadMonth(date?: DateArg<Date> | null | undefined): void;
    canUpdateJournal(type: TrackableType): boolean;
    canUpdateCalendar(): boolean;
    storeJournal(payload: JournalPayload): Promise<void>;
    storePeriod(payload: PeriodPayload): Promise<void>;
    destroyJournal(payload: DestroyJournalPayload): Promise<void>;
    findJournal(id: string): JournalData | null;
    journalsForDay: CalendarListFilter<JournalData>;
    journalsForWeek: CalendarGroupFilter<JournalData>;
    journalsForMonth: CalendarGroupFilter<JournalData>;
    findEvent(id: string): CalendarEventData | null;
    findPrimaryEvent(id: string): CalendarEventData | null;
    storeEvent(payload: CalendarPayload): Promise<void>;
    destroyEvent(id: string): Promise<void>;
    eventsForDay: CalendarListFilter<CalendarEventData>;
    eventsForWeek: CalendarGroupFilter<CalendarEventData>;
    eventsForMonth: CalendarGroupFilter<CalendarEventData>;
    calendarForDay: CalendarListFilter<CalendarEntry>;
    calendarForWeek: CalendarGroupFilter<CalendarEntry>;
    calendarForMonth: CalendarGroupFilter<CalendarEntry>;
};

export const JournalContext = createContext<JournalState>({
    journals: [],
    events: [],
    marked: {},
    recentlyLogged: [],
    modified: false,
    refreshJournals: async (_date) => {
        return;
    },
    refreshEvents: async (_date) => {
        return;
    },
    refreshCalendar: async (_date) => {
        return;
    },
    loadMonth: (_date) => {
        return;
    },
    canUpdateJournal: (_type) => false,
    canUpdateCalendar: () => false,
    storeJournal: async (_payload) => {
        return;
    },
    storePeriod: async (_payload) => {
        return;
    },
    destroyJournal: async (_payload) => {
        return;
    },
    findJournal: (_id) => null,
    journalsForDay: (_date, _search) => [],
    journalsForWeek: (_date, _search) => ({}),
    journalsForMonth: (_date, _search) => ({}),
    findEvent: (_id) => null,
    findPrimaryEvent: (_id) => null,
    storeEvent: async (_payload) => {
        return;
    },
    destroyEvent: async (_id) => {
        return;
    },
    eventsForDay: (_date, _search) => [],
    eventsForWeek: (_date, _search) => ({}),
    eventsForMonth: (_date, _search) => ({}),
    calendarForDay: (_date, _search) => [],
    calendarForWeek: (_date, _search) => ({}),
    calendarForMonth: (_date, _search) => ({}),
});

export function useJournal() {
    const value = useContext(JournalContext);
    if (!value) {
        throw new Error("useJournal must be wrapped in a <JournalProvider />");
    }
    return value;
}

export type JournalProviderProps = {
    profile_id: string | null;
    cycle?: CurrentCycleData;
    children: ReactNode;
} & PermissionProps;

export function JournalProvider({ profile_id, can, cannot, cannotAny, cycle, children }: JournalProviderProps) {
    const { colours } = useTheme();
    const { date } = useContext(CalendarContext);
    const thisMonth = getMonth(date);
    const queryClient = useQueryClient();
    const { client } = useApi();
    const { addToast } = useFeedback();
    const [months, setMonths] = useState([thisMonth]);
    const [modified, setModified] = useState(false);

    const makeJournalKey = useCallback((key: DateArg<Date>) => ["journal", profile_id, key], [profile_id]);
    const canUpdateJournal = useCallback((type: TrackableType) => can("update", journalPermissionSection(type)), [can]);
    const makeCalendarKey = useCallback((key: DateArg<Date>) => ["calendar", profile_id, key], [profile_id]);
    const canUpdateCalendar = useCallback(() => can("update", "calendar"), [can]);

    const refreshJournals = async (date?: DateArg<Date> | null | undefined) => {
        const key = getMonth(date);

        await queryClient.invalidateQueries({ queryKey: makeJournalKey(key), refetchType: "all" });
    };
    const refreshEvents = async (date?: DateArg<Date> | null | undefined) => {
        const key = getMonth(date);

        await queryClient.invalidateQueries({ queryKey: makeCalendarKey(key), refetchType: "all" });
    };

    const refreshCalendar = async (date?: DateArg<Date> | null | undefined) => {
        await Promise.allSettled([refreshJournals(date), refreshEvents(date)]);
    };

    useEffect(() => {
        profile_id && setModified(false);
    }, [profile_id]);

    const combine = useCallback(<T extends CalendarEntry>(results: UseQueryResult<T[]>[]) => {
        const all = results
            .map((result) => result.data ?? [])
            .filter((d) => d.length > 0)
            .sort((a, b) => sortCalendar(a[0], b[0]));

        return {
            data: all.flat(),
            error: results.find((result) => result.error)?.error ?? null,
            isError: results.some((result) => result.isError),
            isLoadingError: results.some((result) => result.isLoadingError),
            isPending: results.some((result) => result.isPending),
            isSuccess: results.every((result) => result.isSuccess),
            isFetched: results.every((result) => result.isFetched),
            isReady: results.some((result) => result.isSuccess) || results.some((result) => result.isFetched),
        };
    }, []);

    const journalQuery = useQueries({
        queries: months.map((month) => ({
            queryKey: makeJournalKey(month),
            queryFn: async (): Promise<JournalData[]> => {
                if (cannotAny("view", ["activities", "symptoms", "journal"])) {
                    return [];
                }

                return await client()
                    .get(`profiles/${profile_id}/journals?month=${month}`)
                    .then((response: AxiosResponse<ListData<JournalData>>) => {
                        return response.data.data;
                    })
                    .catch(() => []);
            },
            staleTime: isBefore(month, thisMonth)
                ? 1000 * 60 * 60 * 24 // 1 day
                : 1000 * 60 * 30, // 30 minutes
            placeholderData: [],
            refetchOnMount: true,
        })),
        combine: combine,
    });
    const journals: CalendarList<JournalData> = useMemo(() => journalQuery.data ?? [], [journalQuery]);

    const handleJournalMutation = useCallback(
        (journal: JournalData) => {
            const monthOf = getMonth(journal.recorded_on);

            queryClient.setQueryData(makeJournalKey(monthOf), (current?: JournalData[]) => {
                return [journal, ...(current ?? []).filter((j) => j.id !== journal.id)].sort((a, b) =>
                    compareDesc(a.recorded_at, b.recorded_at),
                );
            });

            modified || setModified(true);
        },
        [makeJournalKey, queryClient, modified],
    );

    const storeJournalMutation = useMutation({
        mutationFn: async ({ type, id, ...payload }: JournalPayload) => {
            const { method, url } = resolveStoreMethod(`profiles/${profile_id}/journals`, id);

            payload.recorded_at = formatDateTimeStartOfSecond(payload.recorded_at);

            return await client()
                [method](url, payload)
                .then((response: AxiosResponse<JournalData>): JournalData => {
                    addToast({
                        icon: "checkmark-circle",
                        content: `${ucfirst(type)} journal ${response.status === 201 ? "added" : "updated"}`,
                        duration: 3000,
                    });

                    return response.data;
                });
        },
        onSuccess: handleJournalMutation,
    });

    const storePeriodMutation = useMutation({
        mutationFn: async ({ id, ...payload }: PeriodPayload) => {
            const { method, url } = resolveStoreMethod(`profiles/${profile_id}/periods`, id);

            payload.recorded_at = formatDateTimeStartOfSecond(payload.recorded_at);

            return await client()
                [method](url, payload)
                .then((response: AxiosResponse<JournalData>): JournalData => {
                    return response.data;
                });
        },
        onSuccess: handleJournalMutation,
    });

    const destroyJournalMutation = useMutation({
        mutationFn: async ({ id, type, recorded_on }: DestroyJournalPayload) => {
            const url = `profiles/${profile_id}/journals/${id}`;

            return await client()
                .delete(url)
                .then(() => {
                    return { id, type, recorded_on };
                })
                .catch((error: AxiosError) => {
                    switch (error.status) {
                        case 404: {
                            return { id, type, recorded_on };
                        }
                    }

                    throw error;
                });
        },
        onSuccess: ({ id, recorded_on }: DestroyJournalPayload) => {
            const monthOf = getMonth(recorded_on);

            queryClient.setQueryData(
                makeJournalKey(monthOf),
                (current?: JournalData[]) => current?.filter((j) => j.id !== id) ?? [],
            );

            modified || setModified(true);
        },
    });
    const storeJournal = async (payload: JournalPayload) => {
        if (!canUpdateJournal(payload.type)) {
            return;
        }

        await storeJournalMutation.mutateAsync(payload);
    };

    const destroyJournal = async (payload: DestroyJournalPayload) => {
        if (!canUpdateJournal(payload.type)) {
            return;
        }

        await destroyJournalMutation.mutateAsync(payload);
    };
    const storePeriod = async (payload: PeriodPayload) => {
        if (!canUpdateJournal(TrackableType.journal)) {
            return;
        }

        await storePeriodMutation.mutateAsync(payload);
    };

    const eventsQuery = useQueries({
        queries: months.map((month) => ({
            queryKey: makeCalendarKey(month),
            queryFn: async (): Promise<CalendarEventData[]> => {
                if (cannot("view", "calendar")) {
                    return [];
                }

                return await client()
                    .get(`profiles/${profile_id}/events?month=${month}`)
                    .then((response: AxiosResponse<ListData<CalendarEventData>>) => {
                        return response.data.data;
                    })
                    .catch(() => []);
            },
            staleTime: isBefore(month, thisMonth)
                ? 1000 * 60 * 60 * 24 // 1 day
                : 1000 * 60 * 30, // 30 minutes
            placeholderData: [],
            refetchOnMount: true,
        })),
        combine: combine,
    });
    const events: CalendarList<CalendarEventData> = useMemo(() => eventsQuery.data ?? [], [eventsQuery]);
    const calendar: CalendarList = useMemo(() => [...journals, ...events].sort(sortCalendar), [journals, events]);

    const storeEventMutation = useMutation({
        mutationFn: async ({ id, ...payload }: CalendarPayload) => {
            const real_id = id?.replace(/\[[0-9]*]$/, "") ?? null;

            const { method, url } = resolveStoreMethod(`profiles/${profile_id}/events`, real_id);

            payload.start_at = formatDateTimeStartOfMinute(payload.start_at);
            payload.end_at = payload.end_at ? formatDateTimeStartOfMinute(payload.end_at) : null;
            payload.repeat_until = payload.repeat_until ? formatDateString(payload.repeat_until) : null;

            return await client()
                [method](url, payload)
                .then((response: AxiosResponse<CalendarEventData>): CalendarEventData => {
                    addToast({
                        icon: "checkmark-circle",
                        content: `Event ${response.status === 201 ? "added" : "updated"}`,
                        duration: 3000,
                    });

                    return response.data;
                });
        },
        onSuccess: (event: CalendarEventData) => {
            const last_month = endOfMonth(months.sort(compareDesc)[0]);
            const recurrences = resolveRecurrences(event, last_month);

            let occurrences = 0;
            for (const month of months) {
                const dates = recurrences
                    .filter((d) => isSameMonth(d, month))
                    .map(formatDateString)
                    .filter(filterUnique);

                if (filled(dates)) {
                    queryClient.setQueryData(makeCalendarKey(month), (current?: CalendarEventData[]) => {
                        const fill = dates.map((d) => {
                            const is_primary = isSameDay(d, event.start_at);
                            const occurrence = is_primary ? null : ++occurrences;

                            return is_primary
                                ? event
                                : ({
                                      ...event,
                                      id: is_primary ? event.id : `${event.id}[${occurrence}]`,
                                      primary_id: is_primary ? null : event.id,
                                      start_at: formatDateTimeString(mergeDateTime(d, event.start_at)),
                                      end_at: event.end_at
                                          ? formatDateTimeString(mergeDateTime(d, event.end_at))
                                          : null,
                                      occurrence,
                                  } as CalendarEventData);
                        });

                        return [
                            ...fill,
                            ...(current ?? []).filter(({ id, primary_id }) => ![id, primary_id].includes(event.id)),
                        ].sort(sortCalendar);
                    });
                } else {
                    queryClient.setQueryData(makeCalendarKey(month), (current?: CalendarEventData[]) => {
                        return (current ?? []).filter(({ id, primary_id }) => ![id, primary_id].includes(event.id));
                    });
                }
            }

            if (!months.includes(getMonth(event.start_at))) {
                loadMonth(event.start_at);
            }
        },
    });
    const storeEvent = async (payload: CalendarPayload) => {
        if (cannot("update", "calendar")) {
            return;
        }

        await storeEventMutation.mutateAsync(payload);
    };

    const destroyEventMutation = useMutation({
        mutationFn: async (id: string) => {
            const real_id = id.replace(/\[[0-9]*]$/, "");

            return await client()
                .delete(`profiles/${profile_id}/events/${real_id}`)
                .then(() => real_id)
                .catch((error: AxiosError) => {
                    if (error.status === 404) {
                        return real_id;
                    }

                    throw error;
                });
        },
        onSuccess: (id: string) => {
            for (const month of months) {
                queryClient.setQueryData(makeCalendarKey(month), (current?: CalendarEventData[]) => {
                    return (current ?? []).filter((e) => e.id !== id && e.primary_id !== id);
                });
            }
        },
    });
    const destroyEvent = async (id: string) => {
        if (cannot("update", "calendar")) {
            return;
        }

        await destroyEventMutation.mutateAsync(id);
    };

    const findJournal = (id: string) => {
        return journals.find((j) => j.id === id) ?? null;
    };
    const findEvent = (id: string) => {
        return events.find((e) => e.id === id) ?? null;
    };
    const findPrimaryEvent = (id: string) => {
        const event = findEvent(id);

        if (event?.primary_id) {
            return findEvent(event.primary_id);
        }
        return event;
    };

    const journalsForDay: CalendarListFilter<JournalData> = useCallback(
        (date, search) => filterCalendar(journals, date, search),
        [journals],
    );
    const journalsForWeek: CalendarGroupFilter<JournalData> = useCallback(
        (date, search) => filterCalendarForWeek(journals, date, search),
        [journals],
    );
    const journalsForMonth: CalendarGroupFilter<JournalData> = useCallback(
        (date, search) => filterCalendarForMonth(journals, date, search),
        [journals],
    );

    const eventsForDay: CalendarListFilter<CalendarEventData> = useCallback(
        (date, search) => filterCalendar(events, date, search),
        [events],
    );
    const eventsForWeek: CalendarGroupFilter<CalendarEventData> = useCallback(
        (date, search) => filterCalendarForWeek(events, date, search),
        [events],
    );
    const eventsForMonth: CalendarGroupFilter<CalendarEventData> = useCallback(
        (date, search) => filterCalendarForMonth(events, date, search),
        [events],
    );

    const calendarForDay: CalendarListFilter = useCallback(
        (date, search) => filterCalendar(calendar, date, search),
        [calendar],
    );
    const calendarForWeek: CalendarGroupFilter = useCallback(
        (date, search) => filterCalendarForWeek(calendar, date, search),
        [calendar],
    );
    const calendarForMonth: CalendarGroupFilter = useCallback(
        (date, search) => filterCalendarForMonth(calendar, date, search),
        [calendar],
    );

    const loadMonth = (date?: DateArg<Date> | null | undefined) => {
        const key = getMonth(date);

        if (!months.includes(key)) {
            setMonths([key, ...months].sort(compareDesc));
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(loadMonth): API
    useEffect(() => {
        getMonthsForWeek(date).map(loadMonth);
    }, [date]);

    const show_cycle = useMemo(
        () => (cycle?.tracked && can("view", "cycle") && cycle.periods.length ? cycle : null),
        [cycle, can],
    );

    const marked: MarkedDates = useMemo(() => {
        const dates: MarkedDates = {};
        for (const m of months) {
            Object.assign(dates, markDates(calendarForMonth(m), date, colours, show_cycle));
        }
        return dates;
    }, [calendarForMonth, months, date, show_cycle, colours]);

    const recentHistory: DateArg<Date> = subMonths(startOfDay(new Date()), 1);
    const recentlyLogged = journals
        .filter(({ recorded_on }) => isAfter(recorded_on, recentHistory))
        .map(({ trackable_id }) => trackable_id)
        .filter((id, idx, a) => a.indexOf(id) === idx);

    const value = {
        journals,
        events,
        marked,
        recentlyLogged,
        modified,
        loadMonth,
        canUpdateJournal,
        canUpdateCalendar,
        storeJournal,
        storePeriod,
        destroyJournal,
        refreshJournals,
        refreshEvents,
        refreshCalendar,
        findJournal,
        journalsForDay,
        journalsForWeek,
        journalsForMonth,
        storeEvent,
        destroyEvent,
        findEvent,
        findPrimaryEvent,
        eventsForDay,
        eventsForWeek,
        eventsForMonth,
        calendarForDay,
        calendarForWeek,
        calendarForMonth,
        storeJournalMutation,
        destroyJournalMutation,
    };

    return <JournalContext.Provider value={value}>{children}</JournalContext.Provider>;
}
